// geometry.ts — pure placement maths for the product tour. No DOM access:
// callers pass measured rects in, so every edge case (flip, clamp, arrow
// inset, mobile sheet) is unit-testable and the rendered result is exact to
// the pixel rather than "looked fine on my monitor".

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type Side = "top" | "bottom" | "left" | "right";

/** Breakpoint at or below which the card docks to the bottom edge. */
export const SHEET_MAX_WIDTH = 640;
/** Minimum distance between the card and any viewport edge. */
export const VIEWPORT_MARGIN = 12;
/** Distance between the spotlight ring and the card (arrow lives here). */
export const GAP = 14;
/** Spotlight padding around the target. */
export const SPOT_PAD = 6;
/** Arrow never sits closer than this to a card corner (radius + half arrow). */
export const ARROW_INSET = 22;

export interface PlaceInput {
  /** Measured target rect, or null for a centered (anchorless) step. */
  target: Box | null;
  /** Measured card size. */
  tip: { width: number; height: number };
  viewport: { width: number; height: number };
  preferred?: Side;
}

export type Placement =
  | { mode: "center"; top: number; left: number }
  | { mode: "sheet"; spot: Box }
  | {
      mode: "anchored";
      side: Side;
      top: number;
      left: number;
      /** Offset of the arrow along the card edge facing the target. */
      arrow: number;
      /** False when the card had to overlap the spotlight (no side fits). */
      showArrow: boolean;
      spot: Box;
    };

const OPPOSITE: Record<Side, Side> = { top: "bottom", bottom: "top", left: "right", right: "left" };

export function spotlightFor(target: Box, vw: number, vh: number): Box {
  const top = Math.max(0, target.top - SPOT_PAD);
  const left = Math.max(0, target.left - SPOT_PAD);
  const right = Math.min(vw, target.left + target.width + SPOT_PAD);
  const bottom = Math.min(vh, target.top + target.height + SPOT_PAD);
  return { top: Math.round(top), left: Math.round(left), width: Math.round(right - left), height: Math.round(bottom - top) };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(Math.max(n, lo), Math.max(lo, hi));
}

function fits(side: Side, spot: Box, tip: { width: number; height: number }, vw: number, vh: number): boolean {
  switch (side) {
    case "bottom":
      return spot.top + spot.height + GAP + tip.height <= vh - VIEWPORT_MARGIN;
    case "top":
      return spot.top - GAP - tip.height >= VIEWPORT_MARGIN;
    case "right":
      return spot.left + spot.width + GAP + tip.width <= vw - VIEWPORT_MARGIN;
    case "left":
      return spot.left - GAP - tip.width >= VIEWPORT_MARGIN;
  }
}

function room(side: Side, spot: Box, vw: number, vh: number): number {
  switch (side) {
    case "bottom":
      return vh - (spot.top + spot.height);
    case "top":
      return spot.top;
    case "right":
      return vw - (spot.left + spot.width);
    case "left":
      return spot.left;
  }
}

export function placeTip({ target, tip, viewport, preferred = "bottom" }: PlaceInput): Placement {
  const { width: vw, height: vh } = viewport;

  if (!target) {
    return {
      mode: "center",
      top: Math.round(Math.max(VIEWPORT_MARGIN, (vh - tip.height) / 2)),
      left: Math.round(Math.max(VIEWPORT_MARGIN, (vw - tip.width) / 2)),
    };
  }

  const spot = spotlightFor(target, vw, vh);
  if (vw <= SHEET_MAX_WIDTH) return { mode: "sheet", spot };

  const order: Side[] = [preferred, OPPOSITE[preferred], ...(["bottom", "top", "right", "left"] as Side[]).filter((s) => s !== preferred && s !== OPPOSITE[preferred])];
  let side = order.find((s) => fits(s, spot, tip, vw, vh));
  let showArrow = true;
  if (!side) {
    // Nothing fits cleanly (target fills the viewport): take the roomiest
    // side, clamp the card on-screen and drop the arrow rather than point at
    // the wrong place.
    side = [...order].sort((a, b) => room(b, spot, vw, vh) - room(a, spot, vw, vh))[0];
    showArrow = false;
  }

  const cx = spot.left + spot.width / 2;
  const cy = spot.top + spot.height / 2;
  let top: number;
  let left: number;
  if (side === "bottom" || side === "top") {
    left = clamp(cx - tip.width / 2, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tip.width);
    top = side === "bottom" ? spot.top + spot.height + GAP : spot.top - GAP - tip.height;
  } else {
    top = clamp(cy - tip.height / 2, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - tip.height);
    left = side === "right" ? spot.left + spot.width + GAP : spot.left - GAP - tip.width;
  }
  top = clamp(top, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - tip.height);
  left = clamp(left, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - tip.width);

  const arrow =
    side === "bottom" || side === "top"
      ? clamp(cx - left, ARROW_INSET, tip.width - ARROW_INSET)
      : clamp(cy - top, ARROW_INSET, tip.height - ARROW_INSET);

  return { mode: "anchored", side, top: Math.round(top), left: Math.round(left), arrow: Math.round(arrow), showArrow, spot };
}
